import type { Immutable } from "immer";
import React from "react";

/**
 * Inline bed-size SVGs for ``SizeGuideBlockTemplate``, transcribed VERBATIM from
 * the svgo-built ``static/dist2/svg-django/bed-*.svg`` sprites that the legacy
 * ``cms/components/svg.html`` include rendered server-side (the exact source the
 * screenshot baselines were generated from). Inline (not ``react-inlinesvg``)
 * so the markup is present in the SSR/fragment HTML and the ``<svg>`` is a
 * direct child of ``.icon`` (``.icon svg { flex-grow: 1 }``).
 *
 * The legacy ``svg.html`` invocations passed only ``class`` and ``title`` (never
 * ``title_id``/``ariaHidden``), so those Django conditionals collapsed away;
 * each ``<svg>`` keeps ``role="img"``, takes its fixed ``icon--*`` class, and
 * renders a ``<title>`` from the per-icon ``title`` translation.
 */

type SvgProps = Immutable<{
    title: string;
    className: string;
}>;

export const BedTwin = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 48.1 74"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M8.2 0h32c1.7 0 3 1.3 3 3v68c0 1.7-1.3 3-3 3h-32c-1.7 0-3-1.3-3-3V3c0-1.7 1.4-3 3-3"
        />
        <path
            fill="#CCE0E6"
            d="m5.2 32.1 38 13.2V71c0 1.7-1.3 3-3 3h-32c-1.7 0-3-1.3-3-3z"
        />
        <path
            fill="#006481"
            d="M8.2.5V1h32c1.1 0 2 .9 2 2v68c0 1.1-.9 2-2 2h-32c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v68c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3h-32z"
        />
        <path
            fill="#FFF"
            d="M15.2 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L14.7 6c0-.2.2-.4.5-.5l1.7.3h15.5l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H16.8z"
        />
        <path
            fill="#006481"
            d="m34.2 6-1.6.2H16.8L15.2 6l.4 1.8v6.9l-.3 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H17l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.5-1 1-1"
        />
        <path
            fill="#FFF"
            d="M39.5 44.8c-7.1-2.3-24-8.3-31.2-10.8-1.9-.7-3-1.1-3.1-1.1-.7-.2-1-1.3-1.1-2.2v-9c0-.6.3-1 .6-1.2.4-.3.9-.4 1.3-.2l36.4 11.9c.8.3 1.5 1.4 1.6 2.2.1 3.6.1 9.1 0 9.8-.1.4-.2.8-.4 1.1-.1.2-.4.4-1 .4-.1 0-.6-.1-3.1-.9"
        />
        <path
            fill="#006481"
            d="M5.8 20.8c-.3-.1-.6 0-.9.1-.2.2-.3.4-.3.8v9c0 .8.4 1.6.7 1.8.2.1 1.1.4 3.1 1.1 7.2 2.5 24 8.5 31.2 10.8 2.3.7 2.8.9 3 .9.5 0 .6-.1.6-.2.2-.2.2-.6.3-.9.1-.6.1-5.4 0-9.7 0-.6-.6-1.6-1.3-1.8zm.3-1 36.4 11.9c1 .3 1.9 1.7 1.9 2.7 0 0 .2 8.9 0 9.9s-.5 2-1.9 1.9c-.2 0-1.4-.4-3.2-.9-8.9-3-33.5-11.7-34.3-12-1-.3-1.4-1.6-1.4-2.7v-9c0-1.5 1.5-2.1 2.5-1.8"
        />
    </svg>
);

export const BedTwinXl = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 48.1 80"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M8.2 0h32c1.7 0 3 1.3 3 3v74c0 1.7-1.3 3-3 3h-32c-1.7 0-3-1.3-3-3V3c0-1.7 1.4-3 3-3"
        />
        <path
            fill="#CCE0E6"
            d="m5.2 38 38 13.2V77c0 1.7-1.3 3-3 3h-32c-1.7 0-3-1.3-3-3z"
        />
        <path
            fill="#006481"
            d="M8.2.5V1h32c1.1 0 2 .9 2 2v74c0 1.1-.9 2-2 2h-32c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v74c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3h-32z"
        />
        <path
            fill="#FFF"
            d="M15.2 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L14.7 6c0-.2.2-.4.5-.5l1.7.3h15.5l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H16.8z"
        />
        <path
            fill="#006481"
            d="m34.2 6-1.6.2H16.8L15.2 6l.4 1.8v6.9l-.3 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H17l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.5-1 1-1"
        />
        <path
            fill="#FFF"
            d="M39.5 49.7c-7.1-2.3-24-8.3-31.2-10.8-1.9-.7-3-1.1-3.1-1.1-.7-.2-1-1.3-1.1-2.2v-9c0-.6.3-1 .6-1.2.4-.3.9-.4 1.3-.2l36.4 11.9c.8.3 1.5 1.4 1.6 2.2.1 3.6.1 9.1 0 9.8-.1.4-.2.8-.4 1.1-.1.2-.4.4-1 .4-.1 0-.6-.1-3.1-.9"
        />
        <path
            fill="#006481"
            d="M5.8 25.7c-.3-.1-.6 0-.9.1-.2.2-.3.4-.3.8v9c0 .8.4 1.6.7 1.8.2.1 1.1.4 3.1 1.1 7.2 2.5 24 8.5 31.2 10.8 2.3.7 2.8.9 3 .9.5 0 .6-.1.6-.2.2-.2.2-.6.3-.9.1-.6.1-5.4 0-9.7 0-.6-.6-1.6-1.3-1.8zm.3-1 36.4 11.9c1 .3 1.9 1.7 1.9 2.7 0 0 .2 8.9 0 9.9s-.5 2-1.9 1.9c-.2 0-1.4-.4-3.2-.9-8.9-3-33.5-11.7-34.3-12-1-.3-1.4-1.6-1.4-2.7v-9c0-1.5 1.5-2.1 2.5-1.8"
        />
    </svg>
);

export const BedFullDouble = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 63.4 74"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M7.7 0h48c1.7 0 3 1.3 3 3v68c0 1.7-1.3 3-3 3h-48c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="#CCE0E6"
            d="m5.5 26.2 53.2 15v29.3c0 1.9-1.9 3.4-4.2 3.4H9.7c-2.3 0-4.2-1.5-4.2-3.4z"
        />
        <path
            fill="#006481"
            d="M7.7.5V1h48c1.1 0 2 .9 2 2v68c0 1.1-.9 2-2 2h-48c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v68c0 1.7 1.3 3 3 3h48c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3h-48z"
        />
        <path
            fill="#FFF"
            d="M10.7 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L10.2 6c0-.2.2-.4.5-.5l1.7.3H28l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H12.3z"
        />
        <path
            fill="#006481"
            d="m29.7 6-1.6.2H12.3L10.7 6l.4 1.8v6.9l-.3 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3H28l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H12.5l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M34.4 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L34 6c0-.2.2-.4.5-.5l1.7.3h15.5l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3L54 16c0 .2-.2.4-.4.5l-1.2-.4H36z"
        />
        <path
            fill="#006481"
            d="m53.4 6-1.6.2H36L34.5 6l.4 1.8v6.9l-.4 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zM34.5 5l1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4h-16l-1.7.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M53.1 48.5c-10.5-3.4-35-11.8-44.2-15-2.2-.8-3.5-1.2-3.7-1.3-.7-.2-1-1.2-1.1-2.2v-9c0-.6.3-1 .5-1.1.4-.3 1-.4 1.4-.2l51.6 16.8c.9.3 1.6 1.4 1.6 2.2.1 3.6.1 9.1 0 9.8-.2 1-.4 1.5-1.5 1.5 0 0-.7-.2-4.6-1.5"
        />
        <path
            fill="#006481"
            d="M57.5 36.9 5.9 20.2c-.3-.1-.7 0-.9.2s-.3.4-.3.7v9c0 .8.4 1.6.8 1.8.2.1 1.4.5 3.7 1.3 9.2 3.2 33.7 11.6 44.2 15 3.6 1.2 4.5 1.4 4.7 1.4.5 0 .6-.1.6-.2.2-.2.2-.6.3-.9.1-.6 0-6 0-9.7-.2-.7-.8-1.7-1.5-1.9m.3-.9c1.1.3 1.9 1.7 2 2.7 0 0 .2 8.9 0 9.9s-.5 2-2 1.9c-.3 0-2.1-.6-4.9-1.5C40 44.8 5.9 33 5.1 32.7c-1-.3-1.4-1.7-1.5-2.7v-9c0-1.6 1.5-2.2 2.6-1.8z"
        />
    </svg>
);

export const BedQueen = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 69.7 80"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M7.7 0H62c1.7 0 3 1.3 3 3v74c0 1.7-1.3 3-3 3H7.7c-1.7 0-3-1.3-3-3V3c0-1.7 1.4-3 3-3"
        />
        <path
            fill="#CCE0E6"
            d="M5.5 32.2 65 51.6v24.9c0 1.9-2.1 3.4-4.7 3.4H10.2c-2.6 0-4.7-1.5-4.7-3.4z"
        />
        <path
            fill="#006481"
            d="M7.7.5V1H62c1.1 0 2 .9 2 2v74c0 1.1-.9 2-2 2H7.7c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v74c0 1.7 1.3 3 3 3H62c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3H7.7z"
        />
        <path
            fill="#FFF"
            d="M10.7 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L10.2 6c0-.2.2-.4.5-.5l1.7.3H28l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H12.3z"
        />
        <path
            fill="#006481"
            d="m29.7 6-1.6.2H12.3L10.8 6l.4 1.8v6.9l-.4 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3H28l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H12.5l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.5-1 1-1"
        />
        <path
            fill="#FFF"
            d="M40.7 16.5c-.2 0-.4-.2-.5-.4l.4-1.4V7.8L40.2 6c0-.2.2-.4.5-.5l1.7.3H58l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H42.3z"
        />
        <path
            fill="#006481"
            d="m59.7 6-1.6.2H42.3L40.7 6l.4 1.8v6.9l-.3 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3H58l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H42.5l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.5-1 1-1"
        />
        <path
            fill="#FFF"
            d="M58.5 57.2c-12-3.9-39.4-13.3-49.6-16.8-2.2-.7-3.5-1.2-3.6-1.2-.8-.2-1.2-1.3-1.2-2.2v-9c0-.6.3-1 .5-1.1.4-.3 1-.4 1.4-.3l58 18.8c.9.3 1.6 1.4 1.6 2.2.1 3.5.1 9.1 0 9.8-.2 1-.4 1.5-1.5 1.5-.1 0-1-.2-5.6-1.7"
        />
        <path
            fill="#006481"
            d="m63.8 45.9-58-18.8c-.3-.1-.7 0-.9.2s-.3.4-.3.7v9c0 .8.4 1.6.8 1.8.1 0 1.4.5 3.6 1.2 10.3 3.5 37.6 12.8 49.7 16.8 4.3 1.4 5.3 1.7 5.5 1.7.5 0 .6-.1.6-.2.2-.2.2-.6.3-.9.1-.6.1-5.4 0-9.7 0-.6-.6-1.6-1.3-1.8m.3-.9c1.1.3 1.9 1.7 2 2.7 0 0 .1 8.9 0 9.9-.2 1-.5 2-2 1.9-.3 0-2.4-.7-5.7-1.7C43.7 53 5.9 40.1 5.1 39.8c-1-.3-1.4-1.7-1.5-2.7v-9c0-1.6 1.5-2.2 2.6-1.8z"
        />
    </svg>
);

export const BedKing = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 86.1 80"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M8 0h70.2c1.7 0 3 1.3 3 3v74c0 1.7-1.3 3-3 3H8c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="#CCE0E6"
            d="m5.7 32.2 75.4 19.4v24.9c0 1.9-2.7 3.4-6 3.4H11.7c-3.3 0-6-1.5-6-3.4z"
        />
        <path
            fill="#006481"
            d="M8 .5V1h70.2c1.1 0 2 .9 2 2v74c0 1.1-.9 2-2 2H8c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0C6.3 0 5 1.3 5 3v74c0 1.7 1.3 3 3 3h70.2c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3H8z"
        />
        <path
            fill="#FFF"
            d="M10.9 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L10.5 6c0-.2.2-.4.5-.5l1.7.3h15.5l1.8-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H12.5z"
        />
        <path
            fill="#006481"
            d="m30 6-1.6.2H12.5L11 6l.4 1.8v6.9L11 16l1.5-.3h16.4l1 .3-.3-1.5V7.8zM11 5l1.8.3h15.5L30 5c.6 0 1 .4 1 1l-.4 2v6.2L31 16c0 .6-.4 1-1 1l-1.3-.4h-16L11 17c-.6 0-1-.4-1-1l.4-1.5V8L10 6c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M33.9 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L33.5 6c0-.2.2-.4.5-.5l1.7.3h15.5l1.8-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H35.5z"
        />
        <path
            fill="#006481"
            d="m53 6-1.6.2H35.5L34 6l.4 1.8v6.9L34 16l1.5-.3h16.4l1 .3-.3-1.5V7.8zM34 5l1.8.3h15.5L53 5c.6 0 1 .4 1 1l-.4 2v6.2L54 16c0 .6-.4 1-1 1l-1.3-.4h-16L34 17c-.6 0-1-.4-1-1l.4-1.5V8L33 6c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M56.8 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L56.4 6c0-.2.2-.4.5-.5l1.7.3h15.5l1.7-.3c.2 0 .4.2.5.5L76 8v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H58.5z"
        />
        <path
            fill="#006481"
            d="m75.9 6-1.6.2H58.5L56.9 6l.4 1.8v6.9l-.4 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H58.7l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M74.7 60.9c-14-4.6-50.1-16.8-63.7-21.5-3.2-1.1-5.1-1.7-5.3-1.8-1-.3-1.6-1.5-1.7-2.4-.1-.8 0-6.5 0-8.7 0-.4.1-.7.4-.9.5-.4 1.3-.5 2-.2l73.1 23.7c1.1.4 2.2 1.6 2.3 2.5.1 2.5.2 8.9.2 9.7-.1.5-.2.9-.4 1.1-.3.3-.7.4-1.4.3-.1-.1-1-.3-5.5-1.8"
        />
        <path
            fill="#006481"
            d="M79.4 49.5 6.3 25.8c-.5-.2-1.2-.1-1.5.2-.1.1-.2.2-.2.5v.1c-.1 2.9-.1 7.9 0 8.6.1.8.6 1.8 1.3 2 .2.1 2.1.7 5.3 1.8 13.6 4.6 49.7 16.9 63.7 21.5 4.3 1.4 5.3 1.7 5.5 1.7.5 0 .8 0 1-.2s.2-.6.2-.8c.1-.7-.1-6.2-.2-9.6-.1-.6-1-1.7-2-2.1m.3-.9c1.4.4 2.5 1.9 2.7 2.9 0 0 .3 8.8.2 9.8s-.5 2-2.3 1.8c-.3 0-2.4-.7-5.7-1.7-17.3-5.6-68-22.9-69-23.3-1.2-.4-1.9-1.8-2-2.9s0-8.7 0-8.7c-.1-1.6 1.7-2.1 3-1.7z"
        />
    </svg>
);

export const BedCalKing = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 82.4 84"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M8.2 0h65.9c1.7 0 3 1.3 3 3v78c0 1.7-1.3 3-3 3H8.2c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="#CCE0E6"
            d="m5.9 41.9 70.6 23.9v14.7c0 1.9-2.5 3.4-5.6 3.4H11.5c-3.1 0-5.6-1.5-5.6-3.4z"
        />
        <path
            fill="#006481"
            d="M8.2.5V1h65.9c1.1 0 2 .9 2 2v78c0 1.1-.9 2-2 2H8.2c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v78c0 1.7 1.3 3 3 3h65.9c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3H8.2z"
        />
        <path
            fill="#FFF"
            d="M9.1 15.5c-.2 0-.4-.2-.4-.4l.4-1.4V6.8L8.7 5c0-.2.2-.4.5-.5l1.7.3h15.5l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H10.8z"
        />
        <path
            fill="#006481"
            d="m28.2 5-1.6.2H10.8L9.2 5l.4 1.8v6.9L9.2 15l1.5-.3h16.4l1 .3-.3-1.5V6.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H11l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V7l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M32 15.5c-.2 0-.4-.2-.4-.4l.4-1.4V6.8L31.5 5c0-.2.2-.4.5-.5l1.7.3h15.5l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H33.6z"
        />
        <path
            fill="#006481"
            d="m51 5-1.6.2H33.6L32.1 5l.4 1.8v6.9l-.4 1.3 1.5-.3H50l1 .3-.3-1.5V6.8zM32 4l1.8.3h15.5L51 4c.6 0 1 .4 1 1l-.4 2v6.2L52 15c0 .6-.4 1-1 1l-1.3-.4H33.8L32 16c-.6 0-1-.4-1-1l.4-1.5V7L31 5c0-.6.5-1 1-1"
        />
        <path
            fill="#FFF"
            d="M54.8 15.5c-.2 0-.4-.2-.4-.4l.4-1.4V6.8L54.3 5c0-.2.2-.4.5-.5l1.7.3H72l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H56.4z"
        />
        <path
            fill="#006481"
            d="m73.8 5-1.6.2H56.4L54.9 5l.4 1.8v6.9l-.4 1.3 1.5-.3h16.4l1 .3-.3-1.5V6.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H56.6l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V7l-.4-2c0-.6.5-1 1-1"
        />
        <path
            fill="#FFF"
            d="M71.3 64.1C57.9 59.8 23.5 48 10.5 43.6c-3-1-4.9-1.7-5.1-1.7-.8-.3-1.4-1.4-1.4-2.4-.1-.9 0-6.5.1-8.7 0-.4.1-.7.4-1 .4-.3 1.2-.4 1.9-.2l69.7 22.6c1.1.4 2 1.6 2.2 2.4 0 2.5.1 8.9 0 9.7-.1 1-.4 1.5-1.7 1.4-.2 0-1.1-.2-5.3-1.6"
        />
        <path
            fill="#006481"
            d="M76 52.7 6.2 30.1c-.5-.2-1.1-.1-1.4.2-.2.1-.2.3-.2.6v.1c-.1 3-.2 7.8-.1 8.6s.5 1.7 1.2 2c.2.1 2 .7 5.1 1.7 13 4.4 47.4 16.2 60.8 20.5 4.1 1.3 5 1.6 5.2 1.6.3 0 .7 0 .9-.2s.2-.5.2-.8c.1-.7 0-6.2 0-9.6-.2-.7-1-1.8-1.9-2.1m.3-1c1.3.4 2.4 1.8 2.5 2.8 0 0 .1 8.8 0 9.8s-.5 2-2.2 1.9c-.3 0-2.3-.6-5.4-1.7C54.7 59.2 6.3 42.7 5.3 42.4c-1.2-.4-1.8-1.8-1.9-2.8s.1-8.7.1-8.7c-.1-1.6 1.6-2.1 2.9-1.7z"
        />
    </svg>
);

export const BedSplitKing = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 87.7 80"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M8.8 0h32c1.7 0 3 1.3 3 3v77h-35c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="#006481"
            d="M8.8.5V1h32c1.1 0 2 .9 2 2v76h-34c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v74c0 1.7 1.3 3 3 3h34c.6 0 1-.4 1-1V3c0-1.7-1.3-3-3-3h-32z"
        />
        <path
            fill="#FFF"
            d="M15.8 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L15.3 6c0-.2.2-.4.5-.5l1.7.3H33l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H17.4z"
        />
        <path
            fill="#006481"
            d="m34.8 6-1.6.2H17.4L15.8 6l.4 1.8v6.9l-.4 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H17.6l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M46.8 0h32c1.7 0 3 1.3 3 3v74c0 1.7-1.3 3-3 3h-35V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="#006481"
            d="M46.8.5V1h32c1.1 0 2 .9 2 2v74c0 1.1-.9 2-2 2h-34V3c0-1.1.9-2 2-2zV0c-1.7 0-3 1.3-3 3v76c0 .6.4 1 1 1h34c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3h-32z"
        />
        <path
            fill="#FFF"
            d="M53.8 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8L53.3 6c0-.2.2-.4.5-.5l1.7.3H71l1.7-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4H55.4z"
        />
        <path
            fill="#006481"
            d="m72.8 6-1.6.2H55.4L53.8 6l.4 1.8v6.9l-.4 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4H55.6l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M40.1 50c-7.2-2.4-24.3-8.4-31.6-10.9-1.9-.7-3-1.1-3.2-1.1-.7-.2-1.1-1.3-1.1-2.2v-9c0-.6.3-1 .5-1.1.4-.3.9-.4 1.3-.2l36.9 12c.9.3 1.6 1.4 1.6 2.2.1 3.6.1 9.1 0 9.8-.2 1-.4 1.5-1.4 1.5.1-.1-.5-.2-3-1"
        />
        <path
            fill="#006481"
            d="M6 25.8c-.3-.1-.6 0-.9.1-.2.2-.3.4-.3.7v9c0 .8.4 1.6.8 1.8.2.1 1.2.4 3.2 1.1 7.3 2.6 24.3 8.6 31.6 10.9 2.3.7 2.9.9 3 .9.5 0 .6-.1.6-.2.2-.2.2-.6.3-.9.1-.6 0-6 0-9.7 0-.6-.7-1.6-1.3-1.8zm.3-.9 36.9 12c1.1.3 1.9 1.7 2 2.7 0 0 .2 8.9 0 9.9s-.5 2-2 1.9c-.2 0-1.4-.4-3.3-1-9-2.9-33.9-11.7-34.7-12-1-.3-1.4-1.7-1.4-2.7v-9c0-1.5 1.4-2.2 2.5-1.8"
        />
        <path
            fill="#FFF"
            d="M78.6 63C66.1 58.9 23.7 44.3 9.8 39.5 7.1 38.6 5.5 38 5.4 38c-.7-.2-1.1-1.3-1.1-2.2v-9c0-.6.3-1 .5-1.1.4-.3.9-.4 1.3-.2l75.6 25.1c.9.3 1.6 1.4 1.6 2.2.1 3.6.1 9.1 0 9.8-.2 1-.4 1.5-1.4 1.5 0-.1-.6-.2-3.3-1.1"
        />
        <path
            fill="#006481"
            d="M6 25.8c-.3-.1-.6 0-.9.1-.2.2-.3.4-.3.7v9c0 .8.4 1.6.8 1.8.1 0 1.7.6 4.4 1.5 13.9 4.8 56.3 19.4 68.8 23.5 2.4.8 3.1 1 3.3 1 .4 0 .6-.1.6-.2.2-.2.2-.6.3-.9.1-.6 0-6 0-9.7 0-.6-.7-1.6-1.3-1.8zm.3-.9L81.9 50c1.1.3 1.9 1.7 2 2.7 0 0 .2 8.9 0 9.9s-.5 2-2 1.9c-.2 0-1.4-.4-3.5-1.1C63.5 58.5 6 38.6 5.1 38.4c-1-.3-1.4-1.7-1.4-2.7v-9c.1-1.5 1.5-2.2 2.6-1.8"
        />
        <path
            fill="#CCE0E6"
            d="M78.8 79.4h-70c-1.4 0-2.5-1.1-2.5-2.5V37.6l75 24.4v14.9c0 1.4-1.1 2.5-2.5 2.5"
        />
        <path
            fill="#006481"
            d="M6.8 38.3v38.6c0 1.1.9 2 2 2h70c1.1 0 2-.9 2-2V62.3zm-1-1.4 76 24.7v15.3c0 1.7-1.3 3-3 3h-70c-1.7 0-3-1.3-3-3z"
        />
        <path fill="#E5EFF2" d="M44.8 49.4h-.5l.5.2z" />
        <path fill="#B7D3DC" d="M43.8 50.3v28.6h1V50.6z" />
        <path
            fill="#006481"
            d="M44.8 78.9h-1v.5h1zm-.5-29.5h-.5v.9l1 .3v-1.1z"
        />
    </svg>
);

export const BedSplitCaKing = (props: SvgProps): React.JSX.Element => (
    <svg
        className={props.className}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 85.3 84.1"
    >
        <title>{props.title}</title>
        <path
            fill="#FFF"
            d="M8.799 0h32c1.7 0 3 1.3 3 3v81.1h-35c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="none"
            stroke="#006481"
            d="M8.799.5h32c1.4 0 2.5 1.1 2.5 2.5v80.1c0 .3-.2.5-.5.5h-34c-1.4 0-2.5-1.1-2.5-2.5V3c0-1.4 1.1-2.5 2.5-2.5z"
        />
        <path
            fill="#FFF"
            d="M14.799 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8l-.5-1.8c0-.2.2-.4.5-.5l1.7.3h15.5l1.8-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4h-16.3z"
        />
        <path
            fill="#006481"
            d="m33.799 6-1.6.2h-15.8l-1.6-.2.4 1.8v6.9l-.3 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4h-15.9l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M46.799 0h30c1.7 0 3 1.3 3 3v78.1c0 1.7-1.3 3-3 3h-33V3c0-1.7 1.3-3 3-3"
        />
        <path
            fill="none"
            stroke="#006481"
            d="M46.799.5h30c1.4 0 2.5 1.1 2.5 2.5v78.1c0 1.4-1.1 2.5-2.5 2.5h-32c-.3 0-.5-.2-.5-.5V3c0-1.4 1.1-2.5 2.5-2.5z"
        />
        <path
            fill="#FFF"
            d="M52.799 16.5c-.2 0-.4-.2-.4-.4l.4-1.4V7.8l-.5-1.8c0-.2.2-.4.5-.5l1.7.3h15.5l1.8-.3c.2 0 .4.2.5.5l-.3 2v6.3l.3 1.7c0 .2-.2.4-.4.5l-1.2-.4h-16.3z"
        />
        <path
            fill="#006481"
            d="m71.799 6-1.6.2h-15.8l-1.6-.2.4 1.8v6.9l-.3 1.3 1.5-.3h16.4l1 .3-.3-1.5V7.8zm-19-1 1.8.3h15.5l1.7-.3c.6 0 1 .4 1 1l-.4 2v6.2l.4 1.8c0 .6-.4 1-1 1l-1.3-.4h-15.9l-1.8.4c-.6 0-1-.4-1-1l.4-1.5V8l-.4-2c0-.6.4-1 1-1"
        />
        <path
            fill="#FFF"
            d="M40.184 54.096C32.957 51.748 15.898 45.68 8.637 43.11c-1.87-.713-3.012-1.084-3.202-1.146-.666-.216-1.054-1.288-1.06-2.237.023-1.043-.037-8.948-.037-8.948-.005-.633.309-.951.592-1.175.283-.223.82-.26 1.2-.136l36.868 12.085c.856.278 1.53 1.443 1.598 2.201.09 3.604.196 9.106.075 9.802-.12 1.013-.4 1.553-1.443 1.53z"
        />
        <path
            fill="#006481"
            d="M5.976 29.944c-.285-.093-.632.005-.884.133-.157.16-.345.414-.343.73-.03.095.03 8 .037 8.949.038.853.423 1.61.772 1.828.19.062 1.142.37 3.203 1.146 7.26 2.57 24.32 8.638 31.547 10.986 2.282.742 2.853.927 2.979.863.411.028.568-.131.6-.226.156-.16.184-.57.277-.856.09-.602.08-6.072-.01-9.676-.005-.633-.644-1.576-1.31-1.793zm.309-.951 36.868 12.084c1.046.34 1.878 1.662 1.98 2.641 0 0 .163 8.885.044 9.898s-.428 1.964-1.978 1.88c-.221.034-1.457-.368-3.264-.955-9.13-2.966-33.925-11.759-34.78-12.037-.952-.309-1.373-1.602-1.444-2.677s-.005-9.044-.005-9.044c.052-1.455 1.533-2.13 2.579-1.79"
        />
        <path
            fill="#CCE0E6"
            d="M76.899 83.502h-68.2c-1.3 0-2.4-1.1-2.4-2.5v-39.3l73 24.4v14.9c0 1.4-1.1 2.5-2.4 2.5"
        />
        <path
            fill="#006481"
            d="M6.799 42.402v38.6c0 1.1.9 2 1.9 2h68.2c1.1 0 1.9-.9 1.9-2v-14.6zm-1-1.4 74 24.7v15.3c0 1.7-1.3 3-2.9 3h-68.2c-1.6 0-2.9-1.3-2.9-3z"
        />
        <path
            fill="#FFF"
            d="M76.453 66.407C64.28 62.45 23.16 48.249 9.557 43.514a124 124 0 0 0-4.343-1.516c-.666-.217-1.054-1.289-1.06-2.237-.008-.949.122-8.792.058-8.918-.005-.633.309-.951.592-1.175a1.43 1.43 0 0 1 1.326-.2l73.453 24.393c.856.278 1.53 1.443 1.598 2.201-.004 3.573.1 9.075-.02 9.771-.15 1.108-.495 1.522-1.444 1.53-.126.063-.696-.122-3.264-.956"
        />
        <path
            fill="#006481"
            d="M5.976 29.944c-.285-.093-.632.005-.884.133-.157.16-.345.414-.343.73-.03.095-.066 7.97-.059 8.918.039.853.329 1.578.71 1.702.094.031 1.426.464 4.343 1.517 13.506 4.704 54.723 18.937 66.8 22.861l3.139 1.02c.506.06.568-.131.568-.131.188-.254.216-.666.278-.856.09-.601.085-5.44-.01-9.676-.005-.632-.645-1.576-1.215-1.762zm.309-.951 73.453 24.392c1.046.34 1.878 1.662 1.885 2.61 0 0 .068 8.854-.051 9.867s-.523 1.933-1.883 1.911c-.221.034-1.362-.337-3.36-.986C61.75 62.154 5.916 42.751 5.06 42.473c-.95-.309-1.372-1.602-1.348-2.646s.09-9.013.09-9.013c-.043-1.486 1.438-2.16 2.484-1.821"
        />
        <path
            fill="none"
            stroke="#006481"
            d="M44.299 83.502v-28"
            opacity=".097"
        />
    </svg>
);
